"use client";

import { rashis } from "@ishvaram/domain-data/rashis";
import type { Ref } from "react";

import type { ActiveTransit, SadeSatiInfo } from "@/lib/api/transits";
import { SATURN_STATUS_STYLES, dhaiyaBadge, houseOrdinal } from "@/lib/saturn-status";
import { contentQualityAttributes } from "@/lib/seo/content-quality";
import { BELOW_STICKY_CHROME_SCROLL_MARGIN } from "@/lib/sticky-chrome-offsets";

import { SHANI_MOON_CHECK_ANCHOR } from "./_moon-check/shani-moon-check-copy";

/**
 * "uttara_bhadrapada" -> "Uttara Bhadrapada". Inline rather than imported from
 * moon-day-windows, which must stay out of this page's first load
 * (tests/shani-moon-check-island.test.ts).
 */
function nakshatraLabel(id: string): string {
  return id.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/** The fields the position card prints: the engine reply's Saturn entry, or the server's read. */
export type SaturnCardData = Pick<
  ActiveTransit,
  "current_rashi_name_en" | "current_rashi_name_hi" | "rashi_degree" | "nakshatra" | "is_retrograde"
>;

/** Saturn's next sign, labelled on the server from the year datasets. */
export interface NextSignLabel {
  /** "Aries (मेष)". */
  rashi: string;
  /** "June 3, 2027": the first 6:00 am IST reading in the new sign. */
  date: string;
}

/**
 * The Moon-sign result panel's reserve, so the swap from "Checking the live
 * Saturn transit…" to the engine's reply moves nothing below it. Measured
 * 2026-10-07 on next dev with Playwright at 320, 360, 640 and 1280 px over
 * every ready state the engine can send: all 12 Moon signs × all 12 houses,
 * each with its governed Saturn reading (transits.yaml), the Sade Sati phase
 * line for houses 12, 1 and 2 and the "not in the 12th, 1st, or 2nd house"
 * line otherwise. The tallest is 256 px at 320 (Scorpio, 6th house), 200.5 at
 * 360, 146.5 at 640 and 128.5 at 1280; loading is 52. The body font is the
 * system stack, so line breaks differ by platform: 288 and 176 px keep 32 and
 * 29.5 px of slack (under two 18 px lines). With them the panel kept one
 * height through all 132 loading-to-ready swaps at every width measured
 * (also 412, 480, 600, 768 and 1024).
 *
 * Re-measured 2026-10-20 (next dev + Playwright, transits/current stubbed with
 * transits.yaml's Saturn readings) at 320, 360, 390, 412, 640 and 1280 px over
 * all 12 Moon signs x all 12 houses once the Dhaiya badge ("DHAIYA · KANTAKA
 * (4th)" / "DHAIYA · ASHTAMA (8th)") and its line joined the ready states:
 * the tallest is 260 px at 320 (Virgo, 4th house), 238 at 360, 220 at 390,
 * 182.5 at 412 and 146.5 from 640; the empty panel (no sign picked yet) is
 * 114 and 78. The same 288 and 176 px keep 28 and 29.5 px of slack. The panel
 * is now always present at that height, picked or not, so neither a pick nor
 * the Moon-sign check below selecting a sign long after any input moves the
 * page (0 layout shift across every check outcome at all six widths).
 */
const RESULT_RESERVE = "min-h-[18rem] sm:min-h-[11rem]";

/**
 * "Shani is in the 2nd house from Aquarius: <governed reading>", from the
 * engine reply for the picked Moon sign; nothing when the reply lacks a house
 * in 1..12 or a reading.
 */
function saturnHouseLine(saturn: ActiveTransit | undefined, signName: string): string | null {
  const house = saturn?.house_from_moon;
  const reading = saturn?.description_en?.trim();
  if (typeof house !== "number" || !Number.isInteger(house) || house < 1 || house > 12) {
    return null;
  }
  return reading
    ? `Shani is in the ${houseOrdinal(house)} house from ${signName}: ${reading}`
    : null;
}

export function SaturnPositionCard({
  saturnTransit,
  calculationDate,
  nextChange = null,
}: {
  saturnTransit: SaturnCardData | null;
  calculationDate: string;
  nextChange?: NextSignLabel | null;
}) {
  // translate="no": the card swaps from the server's read to the client query.
  // "unavailable" while there is no position, so a server render without one
  // is never stored by the public HTML cache.
  return (
    <div
      translate="no"
      className="card-sacred p-6 mb-5"
      data-engine-state={saturnTransit ? "ready" : "unavailable"}
      {...(saturnTransit
        ? contentQualityAttributes({
            facet: "direct_answer",
            value: "engine",
            engineFacts: "shani-transit-current-position",
          })
        : {})}
    >
      <p className="text-caption uppercase tracking-wider font-semibold mb-1 text-intent-brand">
        Engine-computed current position
      </p>
      {saturnTransit ? (
        <>
          <h2 className="font-serif text-2xl font-bold text-ish-dark">
            Shani in {saturnTransit.current_rashi_name_en} ({saturnTransit.current_rashi_name_hi})
          </h2>
          <p className="mt-1 text-body-small text-ish-text-muted">
            {saturnTransit.rashi_degree.toFixed(1)}° · {nakshatraLabel(saturnTransit.nakshatra)} ·{" "}
            {saturnTransit.is_retrograde ? "Retrograde" : "Direct"}
          </p>
          {nextChange ? (
            <p className="mt-1 text-body-small text-ish-text-muted" data-next-sign="">
              Next sign: {nextChange.rashi}, from 6:00 am IST on {nextChange.date}.
            </p>
          ) : null}
          <p className="mt-3 text-caption text-ish-text-faint">
            Calculated for {calculationDate} from the Vedic transit engine.
          </p>
        </>
      ) : (
        <p className="text-body text-ish-text-muted">Calculating Shani&apos;s live position…</p>
      )}
    </div>
  );
}

/** The badge and first line of a ready panel: Sade Sati phase, Dhaiya, or neither. */
function saturnStatus(
  sadeSati: SadeSatiInfo | null,
  selectedSaturn: ActiveTransit | undefined,
  saturnTransit: ActiveTransit | null
): { key: keyof typeof SATURN_STATUS_STYLES; badge: string; line: string; dhaiya: boolean } {
  if (sadeSati) {
    return {
      key: sadeSati.phase,
      badge: `${sadeSati.phase.toUpperCase()} PHASE`,
      line: sadeSati.phase_description_en,
      dhaiya: false,
    };
  }
  const saturnSign = saturnTransit?.current_rashi_name_en ?? "its current sign";
  const dhaiya = dhaiyaBadge(selectedSaturn);
  if (dhaiya) {
    return {
      key: "dhaiya",
      badge: dhaiya.label,
      line: `Sade Sati is not active. Shani in ${saturnSign} is ${houseOrdinal(dhaiya.house)} from this Moon sign, where the Dhaiya label applies.`,
      dhaiya: true,
    };
  }
  return {
    key: "none",
    badge: "NOT ACTIVE",
    line: `Shani in ${saturnSign} is not in the 12th, 1st, or 2nd house from this Moon sign.`,
    dhaiya: false,
  };
}

const PANEL = `mt-4 rounded-lg p-4 outline-none ${RESULT_RESERVE} ${BELOW_STICKY_CHROME_SCROLL_MARGIN}`;

/** The panel before any pick: same reserve, pointing to the Moon-sign check below. */
function EmptyPanel({ resultRef }: { resultRef?: Ref<HTMLDivElement> }) {
  return (
    <div
      ref={resultRef}
      tabIndex={-1}
      data-saturn-result="empty"
      className={`${PANEL} flex flex-col justify-center border border-dashed border-border-default text-center`}
    >
      <p className="text-body-small text-ish-text-muted">
        Pick your Moon sign above to see which house Shani is in from it.
      </p>
      <p className="mt-2 text-body-small text-ish-text-faint">
        Don&apos;t know your Moon sign?{" "}
        <a href={`#${SHANI_MOON_CHECK_ANCHOR}`} className="inline font-semibold text-intent-brand">
          Find it from your birth details below
        </a>
      </p>
    </div>
  );
}

/** The picked sign's panel: loading, then the engine's status and governed reading. */
function ResultPanel({
  sign,
  sadeSati,
  saturnTransit,
  selectedSaturn,
  pending,
  resultRef,
}: {
  sign: { name_en: string; name_hi: string };
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  selectedSaturn?: ActiveTransit;
  pending: boolean;
  resultRef?: Ref<HTMLDivElement>;
}) {
  const status = pending ? null : saturnStatus(sadeSati ?? null, selectedSaturn, saturnTransit);
  const colors = SATURN_STATUS_STYLES[status?.key ?? "none"];
  const houseLine = saturnHouseLine(selectedSaturn, sign.name_en);
  return (
    <div
      ref={resultRef}
      // Focusable by script only: the Moon-sign check moves focus here when it
      // selects a sign, so focus and the scrolled-to panel stay together.
      tabIndex={-1}
      data-saturn-result={status ? "ready" : "loading"}
      {...(status?.dhaiya ? { "data-saturn-status": "dhaiya" } : {})}
      className={PANEL}
      style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
    >
      {status ? (
        <>
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-body font-bold" style={{ color: colors.text }}>
              {sign.name_en} · {sign.name_hi}
            </span>
            <span
              className="shrink-0 rounded px-2.5 py-1 text-[11px] font-bold"
              style={{ backgroundColor: colors.badgeBg, color: colors.badge }}
            >
              {status.badge}
            </span>
          </div>
          <p className="text-body-small" style={{ color: colors.text }}>
            {status.line}
          </p>
          {houseLine ? (
            <p className="mt-2 text-body-small text-text-primary" data-saturn-house="">
              {houseLine}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-body-small text-ish-text-muted">Checking the live Saturn transit…</p>
      )}
    </div>
  );
}

export function RashiPicker({
  selectedRashi,
  onSelect,
  sadeSati,
  saturnTransit,
  selectedSaturn,
  isLoading,
  resultRef,
}: {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  /** Saturn's entry in the engine reply for the picked Moon sign, once it is in. */
  selectedSaturn?: ActiveTransit;
  isLoading: boolean;
  /** The result panel, which the Moon-sign check below brings into view. */
  resultRef?: Ref<HTMLDivElement>;
}) {
  const selectedData = selectedRashi ? rashis.find((rashi) => rashi.id === selectedRashi) : null;

  // translate="no": the result panel re-renders text on every pick.
  return (
    <div
      translate="no"
      className="card-sacred p-5 mb-5"
      {...contentQualityAttributes({ facet: "primary_task" })}
    >
      <p className="text-body font-semibold mb-3 text-ish-dark">
        Check your Moon sign against Shani&apos;s live position
      </p>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {rashis.map((rashi) => {
          const isSelected = selectedRashi === rashi.id;
          return (
            <button
              key={rashi.id}
              type="button"
              onClick={() => onSelect(isSelected ? null : rashi.id)}
              className="flex flex-col items-center gap-0.5 rounded-lg border p-2 text-center transition-all"
              style={{
                backgroundColor: isSelected
                  ? "var(--ish-accent-light)"
                  : "var(--color-surface-warm)",
                borderColor: isSelected ? "var(--ish-accent)" : "var(--ish-border)",
              }}
            >
              <span className="text-[11px] font-bold leading-tight text-ish-dark">
                {rashi.name_en}
              </span>
              <span className="text-[10px] text-ish-text-faint">{rashi.name_hi}</span>
            </button>
          );
        })}
      </div>

      {/* The panel is always there at the reserved height, so neither a pick
          here nor the Moon-sign check below selecting a sign moves the page. */}
      {selectedData ? (
        <ResultPanel
          sign={selectedData}
          sadeSati={sadeSati}
          saturnTransit={saturnTransit}
          selectedSaturn={selectedSaturn}
          pending={isLoading || sadeSati === undefined}
          resultRef={resultRef}
        />
      ) : (
        <EmptyPanel resultRef={resultRef} />
      )}
    </div>
  );
}
